import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import {
  ArrowLeft, Check, X, Crown, Zap, Rocket, Loader2, CreditCard,
  Calendar, BarChart3, Megaphone, Package, Shield, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";

const PLAN_ICONS = {
  producteur: <Zap className="w-6 h-6" />,
  restaurant: <Rocket className="w-6 h-6" />,
  boutique: <Star className="w-6 h-6" />,
  grande_surface: <Crown className="w-6 h-6" />,
};

const PLAN_COLORS = {
  producteur: "from-green-500 to-emerald-600",
  restaurant: "from-rose-500 to-pink-600",
  boutique: "from-blue-500 to-indigo-600",
  grande_surface: "from-amber-500 to-orange-600",
};

const FEATURE_LIST = [
  { key: "store_profile", label: "Profil boutique", icon: <Package className="w-4 h-4" /> },
  { key: "catalog_limit", label: "Produits au catalogue", icon: <Star className="w-4 h-4" />, format: (v) => v === -1 ? "Illimité" : `${v} max` },
  { key: "campaigns_per_month", label: "Campagnes / mois", icon: <Megaphone className="w-4 h-4" />, format: (v) => v === -1 ? "Illimité" : v === 0 ? "Aucune" : `${v}` },
  { key: "analytics_basic", label: "Statistiques de base", icon: <BarChart3 className="w-4 h-4" /> },
  { key: "analytics_advanced", label: "Statistiques avancées", icon: <BarChart3 className="w-4 h-4" /> },
  { key: "daily_special", label: "Plat du jour", icon: <Star className="w-4 h-4" /> },
  { key: "anti_waste_boxes", label: "Colis anti-gaspi", icon: <Package className="w-4 h-4" /> },
  { key: "menu_management", label: "Gestion du menu", icon: <Package className="w-4 h-4" /> },
  { key: "price_reference_access", label: "Accès prix référence", icon: <Shield className="w-4 h-4" /> },
];

const BusinessSubscriptionPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState("monthly");
  const [checkingOut, setCheckingOut] = useState(null);

  // Handle success redirect
  const sessionId = searchParams.get("session_id");
  const successPlan = searchParams.get("plan");
  const [verifying, setVerifying] = useState(!!sessionId);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchSubscription();
  }, [token]);

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    }
  }, [sessionId]);

  const fetchSubscription = async () => {
    try {
      const res = await fetch(`${API}/business/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSubscription(await res.json());
      }
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sid) => {
    try {
      const res = await fetch(`${API}/business/subscription/verify/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPaymentResult(data);
      if (data.status === "paid") {
        toast.success(data.message);
        fetchSubscription();
      }
    } catch {
      toast.error("Erreur de vérification");
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckout = async (plan) => {
    setCheckingOut(plan);
    try {
      const formData = new FormData();
      formData.append("plan", plan);
      formData.append("billing", billing);
      formData.append("origin_url", window.location.origin);

      const res = await fetch(`${API}/business/subscription/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur");
      }

      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || "Erreur de paiement");
    } finally {
      setCheckingOut(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler votre abonnement ?")) return;
    try {
      const res = await fetch(`${API}/business/subscription/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      toast.success(data.message);
      fetchSubscription();
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading || verifying) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">{verifying ? "Vérification du paiement..." : "Chargement..."}</p>
        </div>
      </div>
    );
  }

  // Payment success screen
  if (paymentResult?.status === "paid") {
    return (
      <div className="min-h-screen pb-24 px-4 pt-8" data-testid="subscription-success">
        <div className="max-w-lg mx-auto text-center space-y-6">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-green-600" />
          </motion.div>
          <h1 className="font-heading text-3xl font-bold text-charcoal">Abonnement activé !</h1>
          <p className="text-muted-foreground">
            Votre forfait <strong>{paymentResult.plan_name}</strong> est maintenant actif.
          </p>
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Forfait</span>
                <span className="font-medium">{paymentResult.plan_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actif jusqu'au</span>
                <span className="font-medium">{new Date(paymentResult.subscription_ends_at).toLocaleDateString("fr-FR")}</span>
              </div>
            </CardContent>
          </Card>
          <Button onClick={() => navigate("/business/dashboard")} className="rounded-full gap-2">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  const currentPlan = subscription?.current_plan || "starter";
  const plans = subscription?.all_plans || {};

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/business/dashboard" className="text-muted-foreground hover:text-charcoal transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-charcoal">Mon abonnement</h1>
            <div className="text-sm text-muted-foreground">
              Forfait actuel : <strong>{plans[currentPlan]?.name || "Starter"}</strong>
              {subscription?.status === "trial" && <Badge className="ml-2 bg-amber-100 text-amber-700">Essai gratuit</Badge>}
              {subscription?.status === "active" && <Badge className="ml-2 bg-green-100 text-green-700">Actif</Badge>}
              {subscription?.status === "cancelling" && <Badge className="ml-2 bg-red-100 text-red-700">Annulé</Badge>}
            </div>
          </div>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1 bg-muted rounded-full p-1" data-testid="billing-toggle">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billing === "monthly" ? "bg-white shadow text-charcoal" : "text-muted-foreground"}`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billing === "yearly" ? "bg-white shadow text-charcoal" : "text-muted-foreground"}`}
            >
              Annuel <span className="text-green-600 text-xs font-bold ml-1">-17%</span>
            </button>
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([key, plan], i) => {
            const isCurrent = key === currentPlan;
            const isPopular = key === subscription?.recommended_plan;
            const price = billing === "yearly" ? plan.price_yearly : plan.price_monthly;
            const monthlyEquiv = billing === "yearly" ? Math.round(plan.price_yearly / 12) : plan.price_monthly;

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className={`relative overflow-hidden transition-shadow hover:shadow-lg ${isPopular ? "ring-2 ring-blue-500 shadow-lg" : ""} ${isCurrent ? "ring-2 ring-green-500" : ""}`}
                  data-testid={`plan-card-${key}`}
                >
                  {isPopular && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      Populaire
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-0 left-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                      Actuel
                    </div>
                  )}

                  <CardHeader className="text-center pb-2 pt-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${PLAN_COLORS[key]} flex items-center justify-center mx-auto text-white mb-3`}>
                      {PLAN_ICONS[key]}
                    </div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </CardHeader>

                  <CardContent className="space-y-5">
                    {/* Price */}
                    <div className="text-center">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-charcoal">{monthlyEquiv}€</span>
                        <span className="text-muted-foreground">/mois</span>
                      </div>
                      {billing === "yearly" && (
                        <p className="text-xs text-green-600 mt-1">Facturé {price}€/an</p>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-3 pt-2 border-t">
                      {FEATURE_LIST.map((feat) => {
                        const val = plan.features?.[feat.key];
                        const active = typeof val === "boolean" ? val : (typeof val === "number" ? val > 0 || val === -1 : false);
                        return (
                          <div key={feat.key} className="flex items-center gap-2.5">
                            {active ? (
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <X className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                            )}
                            <span className={`text-sm ${active ? "text-charcoal" : "text-muted-foreground/50"}`}>
                              {feat.label}
                              {feat.format && active && typeof val !== "boolean" && (
                                <span className="text-muted-foreground ml-1">({feat.format(val)})</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* CTA Button */}
                    <div className="pt-2">
                      {isCurrent ? (
                        <Button variant="outline" className="w-full rounded-full" disabled>
                          Forfait actuel
                        </Button>
                      ) : (
                        <Button
                          className={`w-full rounded-full gap-2 ${isPopular ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                          disabled={checkingOut === key}
                          onClick={() => handleCheckout(key)}
                          data-testid={`checkout-${key}`}
                        >
                          {checkingOut === key ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CreditCard className="w-4 h-4" />
                          )}
                          {currentPlan === "starter" || Object.keys(plans).indexOf(key) > Object.keys(plans).indexOf(currentPlan) ? "Souscrire" : "Changer"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Current subscription info */}
        {subscription?.status === "active" && (
          <Card className="border-green-200 bg-green-50/50" data-testid="current-sub-info">
            <CardContent className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${PLAN_COLORS[currentPlan]} flex items-center justify-center text-white`}>
                    {PLAN_ICONS[currentPlan]}
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">Abonnement {plans[currentPlan]?.name} actif</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {subscription?.subscription_ends_at
                        ? `Renouvellement le ${new Date(subscription.subscription_ends_at).toLocaleDateString("fr-FR")}`
                        : "Période d'essai"}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full" onClick={handleCancel}>
                  Annuler l'abonnement
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BusinessSubscriptionPage;
